import { scry } from "@/lib/scryClient";
// CommunityFeed.tsx (rebased from CommunityFeedPublic + Cards "marketplace" view)
// (Normalized to pure v9 Firestore modular usage with aliased orderBy)
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { auth, db } from "@/lib/firebase";
import {
  getFirestore,
  collection,
  collectionGroup,
  getDocs,
  query,
  where,
  orderBy as fbOrderBy,   // aliased to ensure Firestore orderBy is used
  limit,
  startAfter,
  doc,
  getDoc,
  type QueryDocumentSnapshot,
  type DocumentData
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import NewMiniProfileCard from "@/components/community/NewMiniProfileCard";
import { Button } from "@/components/ui/button";
import ListingProfileCard from "@/components/community/ListingProfileCard";
import EventsPage from "@/pages/Events"; // already added

// Works whether `db` is compat or modular. Falls back to default app if needed.
const db9 = ((db as any)?._delegate ?? db) || getFirestore();

/* ------------------------------- Constants ------------------------------- */
const MTG_FORMATS = [
  "Commander","cEDH","Canadian Highlander","Modern","Standard","Pioneer","Legacy","Vintage",
  "Pauper","Draft","Sealed","MTG Arena","Explorer","Historic","Alchemy","D&D (non-MTG)"
];

// map friendly format names -> scryfall/legalities keys (best-effort)
const FORMAT_KEY_MAP: Record<string, string> = {
  Commander: "commander",
  cEDH: "commander",
  Modern: "modern",
  Standard: "standard",
  Pioneer: "pioneer",
  Legacy: "legacy",
  Vintage: "vintage",
  Pauper: "pauper",
  "MTG Arena": "arena",
  Explorer: "explorer",
  Historic: "historic",
  Alchemy: "alchemy",
  // fallback will use a simplified normalization
};

const HIDDEN_USERNAMES = new Set(["scoreboard-bot"]);
const HIDDEN_OWNER_UIDS = new Set(["scoreboard-bot"]); // assume UID matches username

type Profile = {
  uid: string;
  username?: string | null;
  display_name?: string | null;
  avatar_url?: string | null;
  rank_title?: string | null;
  preferences?: { available_for_trading?: boolean; available_for_play?: boolean; };
  location?: { lat?: number | null; lng?: number | null };
  play_formats?: string[];
};

type ViewMode = "feed" | "grid" | "cards" | "events"; // ADDED
const PAGE = 48;
// We fetch up to ~60 cards per load (3 lanes × 20 each) but show 24 at a time in the UI.
const CARDS_PAGE_SIZE = 20;

function useDebounced<T>(v: T, ms = 250) {
  const [d, setD] = useState(v);
  useEffect(() => { const t = setTimeout(() => setD(v), ms); return () => clearTimeout(t); }, [v, ms]);
  return d;
}

/* --------------------------- Cards view types ---------------------------- */
type GlobalListing = {
  id: string;
  ownerUid: string;
  name: string;
  name_lower: string;
  image: string | null;
  set: string | null;
  number: string | number | null;
  foil: boolean;
  status: "sale" | "trade";
  price: number | null;
  scryfall_id: string | null;
  oracle_id: string | null;
  createdAt?: any;
};
type CardsListingExt = GlobalListing & {
  createdAtMs: number;
  updatedAtMs: number;
  lastActivityMs: number;
  distance?: number;
  legalities?: Record<string, string>;
  types?: string[];
};

/* ------------------------------ Component -------------------------------- */
export default function CommunityFeedPublic(): JSX.Element {
  /* ------------------------------- View mode ------------------------------ */
  const [view, setView] = useState<ViewMode>("feed");
  const [showCalendar, setShowCalendar] = useState(false);
  const calendarRef = useRef<HTMLDivElement | null>(null);
  const calendarToggleRef = useRef<HTMLButtonElement | null>(null);

  // Collapsible preferences toolbar (collapsed by default)
  const [toolbarCollapsed, setToolbarCollapsed] = useState<boolean>(true);

  /* --------------------------- READ-ONLY GATING --------------------------- */
  // Force public mode: no authenticated user context, no interactive mutations.
  const READ_ONLY = true;
  const canInteract = false;
  // Hard-null auth user (ignore real auth state)
  const meUid: string | null = null;

  // Click‑away + Esc to close calendar
  useEffect(() => {
    if (!showCalendar) return;
    function onPointer(e: MouseEvent | TouchEvent) {
      const tgt = e.target as Node;
      if (calendarRef.current && calendarRef.current.contains(tgt)) return;
      if (calendarToggleRef.current && calendarToggleRef.current.contains(tgt)) return;
      setShowCalendar(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setShowCalendar(false);
    }
    document.addEventListener("mousedown", onPointer, true);
    document.addEventListener("touchstart", onPointer, true);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer, true);
      document.removeEventListener("touchstart", onPointer, true);
      document.removeEventListener("keydown", onKey);
    };
  }, [showCalendar]);

  /* ------------------------------- Filters -------------------------------- */
  const [search, setSearch] = useState("");
  const debounced = useDebounced(search, 250);
  const [eventDate, setEventDate] = useState<Date | null>(null);

  /* ------------------------------- Global Radius -------------------------- */
  // Radius fixed (read-only)
  const [radiusMi] = useState<number | null>(null);

  const [visibilityFilter, setVisibilityFilter] =
    useState<"all" | "public" | "private">("public"); // only public in read-only

  const [selectedProfileUid, setSelectedProfileUid] = useState<string | null>(null);
  const [profileSelectOpen, setProfileSelectOpen] = useState(false);

  const [expandedProfileUid, setExpandedProfileUid] = useState<string | null>(null);
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setExpandedProfileUid(null);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  useEffect(() => {
    if (!expandedProfileUid) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [expandedProfileUid]);

  useEffect(() => {
    function onCommunityFilter(e: any) {
      if (!canInteract) return;
      const term = (e?.detail?.term ?? "").toString();
      setSearch(term);
    }
    window.addEventListener("app:communityFilter", onCommunityFilter as any);
    return () => window.removeEventListener("app:communityFilter", onCommunityFilter as any);
  }, [canInteract]);

  const [formats, setFormats] = useState<string[]>([]);
  const toggleFormat = useCallback((f: string) => {
    if (!canInteract) return;
    setFormats(prev => prev.includes(f) ? prev.filter(x => x !== f) : prev.concat(f));
  }, [canInteract]);
  const clearFilters = () => {
    // Disabled in read-only (no-op)
  };

  const [cardTypes, setCardTypes] = useState<string[]>([]);
  const toggleCardType = useCallback((t: string) => {
    if (!canInteract) return;
    setCardTypes(prev => (prev.includes(t) ? prev.filter(x => x !== t) : prev.concat(t)));
  }, [canInteract]);

  const [excludeSelf, setExcludeSelf] = useState<boolean>(false);

  const [lgsFilter, setLgsFilter] = useState<"all" | "only" | "hide">("all");
  const cycleLgs = () => {
    if (!canInteract) return;
  };

  /* ------------------------------ Auth user ------------------------------- */
  // (Auth listener removed for read-only)

  /* ------------------------------ Location -------------------------------- */
  // (Keep geolocation to show distance if user approves; optional)
  const [myLoc, setMyLoc] = useState<{ lat: number; lng: number } | null>(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Skip user profile location fetch (no auth)
      } catch {}
      if (!cancelled && navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          pos => !cancelled && setMyLoc({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
          () => {}
        );
      }
    })();
    return () => { cancelled = true; };
  }, []);

  /* ------------------------------ Profiles data --------------------------- */
  const [rows, setRows] = useState<Profile[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const cursorRef = useRef<QueryDocumentSnapshot<DocumentData> | null>(null);
  const loadingRef = useRef(false);

  const loadProfiles = useCallback(async (initial = false) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    try {
      if (initial) cursorRef.current = null;
      const base = query(                                    // use db9 + fbOrderBy
        collection(db9, "users"),
        fbOrderBy("__name__"),
        limit(PAGE)
      );
      const qy = cursorRef.current ? query(base, startAfter(cursorRef.current)) : base;
      const snap = await getDocs(qy);
      if (snap.docs.length) cursorRef.current = snap.docs.at(-1)!;
      const page: Profile[] = snap.docs.map(d => ({ uid: d.id, ...(d.data() as any) }));
      const pageFiltered = page.filter(p => {
        const uname = (p.username || p.display_name || "").toLowerCase();
        return !HIDDEN_USERNAMES.has(uname);
      });
      setRows(prev => {
        const map = new Map<string, Profile>();
        (initial ? [] : prev).forEach(p => map.set(p.uid, p));
        pageFiltered.forEach(p => map.set(p.uid, p));
        return Array.from(map.values());
      });
      setHasMore(snap.docs.length === PAGE);
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, []);

  useEffect(() => {
    setRows([]); setHasMore(false); cursorRef.current = null;
    void loadProfiles(true);
  }, [loadProfiles]);

  /* ------------------------------ Distance util (now module scope) --------------------------- */
  const R_EARTH_MI = 3958.7613;
  const toRad = (d: number) => (d * Math.PI) / 180;
  function distMiles(aLat: number, aLng: number, bLat: number, bLng: number) {
    const dLat = toRad(bLat - aLat), dLon = toRad(bLng - aLng);
    const s = Math.sin, c = Math.cos;
    const h = s(dLat/2)**2 + c(toRad(aLat))*c(toRad(bLat))*s(dLon/2)**2;
    return 2 * R_EARTH_MI * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
  }


  // --- START PATCH: SCRYFALL BACKEND HELPERS (PUBLIC) ---
/** Defensive JSON unwrap */
async function toJsonOrNull(res: Response | null | undefined) {
  try {
    if (!res || !("ok" in res) || !res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

/** Prefer normal/large; supports multi-face; skip when image_status === "missing" */
function pickScryImage(sf: any): string | null {
  if (!sf || sf.image_status === "missing") return null;
  const direct = sf?.image_uris?.normal ?? sf?.image_uris?.large ?? null;
  if (direct) return direct;
  if (Array.isArray(sf?.card_faces)) {
    for (const face of sf.card_faces) {
      const img = face?.image_uris?.normal ?? face?.image_uris?.large ?? null;
      if (img) return img;
    }
  }
  return null;
}

/** Tiny per-run cache to avoid duplicate oracle fetches */
const __oracleFetchCache = new Map<string, Promise<any | null>>();

/** Backend-first fetch by oracle_id, fallback to public Scryfall */
async function fetchByOracleBackendFirst(oracleId: string): Promise<any | null> {
  if (__oracleFetchCache.has(oracleId)) return __oracleFetchCache.get(oracleId)!;

  const p = (async () => {
    // Try backend client – feature detect likely methods
    try {
      if (typeof (scry as any)?.getByOracleId === "function") {
        const res = await (scry as any).getByOracleId(oracleId);
        const json = await toJsonOrNull(res);
        if (json?.id) return json;
      }
      if (typeof (scry as any)?.getCard === "function") {
        const res = await (scry as any).getCard({ oracle_id: oracleId });
        const json = await toJsonOrNull(res);
        if (json?.id) return json;
      }
    } catch {
      // ignore & fallback
    }

    // Public fallback
    try {
      const r = await fetch(`https://api.scryfall.com/cards/oracle/${encodeURIComponent(oracleId)}`);
      return await toJsonOrNull(r);
    } catch {
      return null;
    }
  })();

  __oracleFetchCache.set(oracleId, p);
  return p;
}
// --- END PATCH: SCRYFALL BACKEND HELPERS (PUBLIC) ---

  /* ------------------------------- Cards view ----------------------------- */
  const [cardsListings, setCardsListings] = useState<CardsListingExt[]>([]);
  const [cardsLoading, setCardsLoading] = useState(false);
  const cardsLoadingRef = useRef(false);
  const [cardsHasMore, setCardsHasMore] = useState(false);
  const [cardsError, setCardsError] = useState<string | null>(null);

  // Cursor refs for the 3 marketplace lanes (status / for_sale / for_trade)
  const cardsCursorsRef = useRef<{
    status: QueryDocumentSnapshot<DocumentData> | null;
    flagSale: QueryDocumentSnapshot<DocumentData> | null;
    flagTrade: QueryDocumentSnapshot<DocumentData> | null;
  }>({ status: null, flagSale: null, flagTrade: null });

  // Reset helper used before initial / re-loads
  function resetCardsCursorsAll() {
    cardsCursorsRef.current = { status: null, flagSale: null, flagTrade: null };
  }


  // NEW: distance owner location caches (fix ownerLocs ReferenceError)
  const [ownerLocs, setOwnerLocs] = useState<Record<string, { lat: number; lng: number } | null>>({});
  const ownerLocsRef = useRef(ownerLocs);
  useEffect(() => { ownerLocsRef.current = ownerLocs; }, [ownerLocs]);
  const myLocRef = useRef(myLoc);
  useEffect(() => { myLocRef.current = myLoc; }, [myLoc]);

  // NEW: per‑owner filter + visible count (24 at a time)
  const [cardOwnerFilter, setCardOwnerFilter] = useState<string | null>(null);
  const [showAllOwners, setShowAllOwners] = useState(true); // NEW toggle
  const [cardsVisibleCount, setCardsVisibleCount] = useState(24);

  // Reset visible slice when core filters change (keep smooth UX)
  useEffect(() => {
    if (view === "cards") setCardsVisibleCount(24);
  }, [view, search, debounced, excludeSelf, cardOwnerFilter, showAllOwners, formats, cardTypes]);

  // Helper to resolve owner display name from already-loaded profiles
  const ownerName = useCallback(
    (uid: string) => {
      const p = rows.find(r => r.uid === uid);
      return p?.display_name || p?.username || uid.slice(0, 6);
    },
    [rows]
  );

  // (Owner avatar optional; not required but can be uncommented if needed)
  // const ownerAvatar = useCallback(
  //   (uid: string) => rows.find(r => r.uid === uid)?.avatar_url || null,
  //   [rows]
  // );

  // NEW: listen for QuickSearchOverlay community filter events (immediate update)
  useEffect(() => {
    function onCommunityFilter(e: any) {
      const term = (e?.detail?.term ?? "").toString();
      setSearch(term); // immediate so Cards view reflects instantly
    }
    window.addEventListener("app:communityFilter", onCommunityFilter as any);
    return () => window.removeEventListener("app:communityFilter", onCommunityFilter as any);
  }, []);

  // Marketplace triple-lane loader
  const loadCards = useCallback(async (initial: boolean) => {
    if (cardsLoadingRef.current) return;
    cardsLoadingRef.current = true;
    setCardsLoading(true);

    if (initial) {
      resetCardsCursorsAll();
      setCardsListings([]);
      setCardsError(null);
    }

    try {
      // Use a larger per-lane page for the initial load (like Marketplace)
      const LANE_PAGE = initial ? PAGE : CARDS_PAGE_SIZE;

      // We'll iterate fetches on initial load until we collect a useful number
      const ownerFromPath = (path: string) => {
        const segs = path.split("/");
        const uIdx = segs.indexOf("users");
        if (uIdx >= 0 && segs[uIdx + 1]) return segs[uIdx + 1];
        const cIdx = segs.lastIndexOf("cards");
        if (cIdx > 0 && segs[cIdx - 1]) return segs[cIdx - 1];
        return null;
      };

      const KNOWN_TYPES = ["creature","instant","sorcery","enchantment","artifact","planeswalker","battle","land"];
      const assimilateDoc = (d: QueryDocumentSnapshot<DocumentData>, out: Map<string, CardsListingExt>) => {
        const v: any = d.data();
        const ownerUid = ownerFromPath(d.ref.path) || v.ownerUid || "unknown";
        if (HIDDEN_OWNER_UIDS.has(ownerUid)) return;
        if (excludeSelf && meUid && ownerUid === meUid) return;

        const name = v.name || v.card_name || "Card";
        const legalities: Record<string, string> =
          v.scryfall_full?.legalities || v.legalities || {};
        const typeLine: string = v.type_line || v.scryfall_full?.type_line || (v.card_type || "") || "";
        const typeParts = typeLine.toLowerCase().split(/[^a-z]+/).filter(Boolean);
        const types = typeParts.filter(p => KNOWN_TYPES.includes(p));
        const createdAtMs =
          v.createdAt?.toMillis?.() ?? (typeof v.createdAt === "number" ? v.createdAt : 0);
        const updatedAtMs =
          v.updatedAt?.toMillis?.() ?? (typeof v.updatedAt === "number" ? v.updatedAt : createdAtMs);
        const status: "sale" | "trade" =
          v.status === "sale" || v.status === "trade" ? v.status : v.for_sale === true ? "sale" : "trade";

        const entry: CardsListingExt = {
          id: d.id,
          ownerUid,
          name,
          name_lower: (v.name_lower || name).toLowerCase(),
          image:
            v.custom_image_url ||
            v.image ||
            v.card_image ||
            v.scryfall_full?.image_uris?.normal ||
            v.scryfall_full?.image_uris?.small ||
            null,
          set: v.set ?? v.set_code ?? null,
          number: v.number ?? v.collector_number ?? null,
          foil: !!(v.foil || v.isFoil),
          status,
          price: typeof v.price === "number" ? v.price : null,
          scryfall_id: v.scryfall_id || null,
          oracle_id: v.oracle_id || null,
          createdAt: v.createdAt || null,
          createdAtMs,
          updatedAtMs,
          lastActivityMs: createdAtMs,
          legalities,
          types,
          distance: undefined,
        };
        out.set(`${entry.ownerUid}:${entry.id}`, entry);
      };

      // Prepare bases once (limit will be applied per-iteration via LANE_PAGE)
      const statusBase = query(
        collectionGroup(db9, "cards"),
        where("status", "in", ["sale", "trade"]),
        fbOrderBy("createdAt", "desc"),
        limit(LANE_PAGE)
      );
      const saleBase = query(
        collectionGroup(db9, "cards"),
        where("for_sale", "==", true),
        fbOrderBy("createdAt", "desc"),
        limit(LANE_PAGE)
      );
      const tradeBase = query(
        collectionGroup(db9, "cards"),
        where("for_trade", "==", true),
        fbOrderBy("createdAt", "desc"),
        limit(LANE_PAGE)
      );

      const cumulative = new Map<string, CardsListingExt>();
      let iterations = 0;
      const TARGET = 24;
      let anyLaneFilledLast = false;
      do {
        const qStatus = cardsCursorsRef.current.status
          ? query(statusBase, startAfter(cardsCursorsRef.current.status))
          : statusBase;
        const qSale = cardsCursorsRef.current.flagSale
          ? query(saleBase, startAfter(cardsCursorsRef.current.flagSale))
          : saleBase;
        const qTrade = cardsCursorsRef.current.flagTrade
          ? query(tradeBase, startAfter(cardsCursorsRef.current.flagTrade))
          : tradeBase;

        const [snapStatus, snapSale, snapTrade] = await Promise.all([
          getDocs(qStatus),
          getDocs(qSale),
          getDocs(qTrade),
        ]);

        anyLaneFilledLast =
          snapStatus.docs.length === LANE_PAGE ||
          snapSale.docs.length === LANE_PAGE ||
          snapTrade.docs.length === LANE_PAGE;

        if (snapStatus.docs.length) cardsCursorsRef.current.status = snapStatus.docs.at(-1)!;
        if (snapSale.docs.length) cardsCursorsRef.current.flagSale = snapSale.docs.at(-1)!;
        if (snapTrade.docs.length) cardsCursorsRef.current.flagTrade = snapTrade.docs.at(-1)!;

        for (const d of snapStatus.docs) assimilateDoc(d, cumulative);
        for (const d of snapSale.docs) assimilateDoc(d, cumulative);
        for (const d of snapTrade.docs) assimilateDoc(d, cumulative);

        iterations++;
      } while (initial && iterations < 4 && cumulative.size < TARGET && anyLaneFilledLast);

      const merged = Array.from(cumulative.values());
      setCardsHasMore(anyLaneFilledLast);

     // --- START PATCH: SCRYFALL BACKEND ENRICH (PUBLIC) ---
      // Enrich via backend-first Scryfall (legalities/types/image), fallback to public
      try {
        const need = new Map<string, string>(); // oracle_id -> key(owner:id)
        for (const c of merged) {
          const lacksLegal = !c.legalities || Object.keys(c.legalities).length === 0;
          const lacksTypes = !c.types || c.types.length === 0;
          const lacksImage = !c.image;
          if ((lacksLegal || lacksTypes || lacksImage) && c.oracle_id) {
            need.set(c.oracle_id, `${c.ownerUid}:${c.id}`);
          }
        }

        if (need.size) {
          const fetches = Array.from(need.keys()).map(async (oid) => {
            const data = await fetchByOracleBackendFirst(oid);
            return data ? { oid, data } : null;
          });

          const results = await Promise.all(fetches);
          const byOid = new Map<string, any>();
          for (const r of results) if (r && r.oid && r.data) byOid.set(r.oid, r.data);

          if (byOid.size) {
            for (let i = 0; i < merged.length; i++) {
              const c = merged[i];
              if (!c.oracle_id) continue;
              const s = byOid.get(c.oracle_id);
              if (!s) continue;

              // legalities
              if (!c.legalities || Object.keys(c.legalities).length === 0) {
                if (s.legalities && typeof s.legalities === "object") {
                  c.legalities = { ...s.legalities };
                }
              }

              // types (from type_line)
              if (!c.types || c.types.length === 0) {
                const typeLine = (s.type_line || "").toString().toLowerCase();
                const parts = typeLine.split(/[^a-z]+/).filter(Boolean);
                const KNOWN_TYPES = ["creature","instant","sorcery","enchantment","artifact","planeswalker","battle","land"];
                const types = parts.filter(p => KNOWN_TYPES.includes(p));
                if (types.length) c.types = types;
              }

              // image (only if missing)
              if (!c.image) {
                const img = pickScryImage(s);
                if (img) c.image = img;
              }
            }
          }
        }
      } catch (e) {
        console.debug("[CommunityFeedPublic] scryfall enrich (backend-first) failed", e);
      }
// --- END PATCH: SCRYFALL BACKEND ENRICH (PUBLIC) ---

      // Merge into state (initial replaces, subsequent appends/merge)
      setCardsListings(prev => {
        const map = new Map<string, CardsListingExt>();
        (initial ? [] : prev).forEach(c => map.set(`${c.ownerUid}:${c.id}`, c));
        merged.forEach(c => map.set(`${c.ownerUid}:${c.id}`, c));
        let arr = Array.from(map.values());
        if (myLocRef.current) {
          arr = arr.map(c => {
            const loc = ownerLocsRef.current[c.ownerUid];
            if (!loc) return c;
            const dmi = distMiles(myLocRef.current.lat, myLocRef.current.lng, loc.lat, loc.lng);
            return { ...c, distance: dmi };
          });
        }
        arr.sort((a, b) =>
          b.createdAtMs - a.createdAtMs ||
          (a.distance ?? Infinity) - (b.distance ?? Infinity) ||
          a.name_lower.localeCompare(b.name_lower)
        );
        return arr;
      });
      if (initial) setCardsVisibleCount(Math.min(24, Math.max(merged.length, cumulative.size)));

    } catch (e: any) {
      console.error("[CommunityFeed] loadCards failed:", e);
      const code = String(e?.code || "").toLowerCase();
      if (code.includes("permission")) setCardsError("Permission denied (rules).");
      else if (code.includes("failed-precondition")) setCardsError("Missing index for marketplace queries.");
      else setCardsError("Failed to load cards.");
    } finally {
      setCardsLoading(false);
      cardsLoadingRef.current = false;
    }
  }, [excludeSelf, meUid]);

// --- START PATCH: INITIAL LOAD FIX (PUBLIC) ---
useEffect(() => {
  if (view === "cards" && cardsListings.length === 0 && !cardsLoading) {
    void loadCards(true);
  }
}, [view, cardsListings.length, cardsLoading, loadCards]);
// --- END PATCH: INITIAL LOAD FIX (PUBLIC) ---


  // Updated excludeSelf effect to use new reset
  useEffect(() => {
    if (view !== "cards") return;
    resetCardsCursorsAll();
    setCardsListings([]);
    void loadCards(true);
  }, [excludeSelf, view, loadCards]);

  // Resort distances when location/owner locations change
  useEffect(() => {
    if (!cardsListings.length) return;
    setCardsListings(prev => {
      const arr = prev.map(c => {
                   if (!myLocRef.current) return c;
          const owners = ownerLocsRef.current;
          if (!owners) return c;
          const loc = owners[c.ownerUid];
          if (!loc) return c;
        const dmi = distMiles(
          myLocRef.current.lat,
          myLocRef.current.lng,
          loc.lat,
          loc.lng
        );
        return { ...c, distance: dmi };
      }).sort((a, b) =>
        b.createdAtMs - a.createdAtMs ||
        (a.distance ?? Infinity) - (b.distance ?? Infinity) ||
        a.name_lower.localeCompare(b.name_lower)
      );
      return arr;
    });
  }, [ownerLocs, myLoc, cardsListings.length]);

  /* ------------------------------ Profiles filtering ---------------------- */
  const filteredProfiles = useMemo(() => {
    let list = rows;

    if (formats.length) {
      const pick = new Set(formats.map(s => s.toLowerCase().trim()));
      list = list.filter(p => {
        const they = (p.play_formats || []).map(s => (s || "").toLowerCase().trim());
        for (const f of pick) if (they.includes(f)) return true;
        return false;
      });
    }

    if (debounced.trim()) {
      const q = debounced.trim().toLowerCase();
      list = list.filter(p =>
        (p.username || "").toLowerCase().includes(q) ||
        (p.display_name || "").toLowerCase().includes(q)
      );
    }

    if (excludeSelf && meUid) {
      list = list.filter(p => p.uid !== meUid);
    }

    if (lgsFilter !== "all") {
      list = list.filter(p =>
        lgsFilter === "only" ? isLgsProfile(p) : !isLgsProfile(p)
      );
    }

    if (radiusMi != null && myLoc) {
      list = list.filter(p => {
        const plat = typeof p.location?.lat === "number" ? p.location.lat : null;
        const plng = typeof p.location?.lng === "number" ? p.location.lng : null;
        if (plat == null || plng == null) return false;
        return distMiles(myLoc.lat, myLoc.lng, plat, plng) <= radiusMi;
      });
    }

    if (myLoc) {
      list = [...list].sort((a, b) => {
        const aLat = typeof a.location?.lat === "number" ? a.location.lat : null;
        const aLng = typeof a.location?.lng === "number" ? a.location.lng : null;
        const bLat = typeof b.location?.lat === "number" ? b.location.lat : null;
        const bLng = typeof b.location?.lng === "number" ? b.location.lng : null;
        const ad = aLat != null && aLng != null ? distMiles(myLoc.lat, myLoc.lng, aLat, aLng) : Infinity;
        const bd = bLat != null && bLng != null ? distMiles(myLoc.lat, myLoc.lng, bLat, bLng) : Infinity;
        return ad - bd;
      });
    }

    if (selectedProfileUid) {
      list = list.filter(p => p.uid === selectedProfileUid);
    }

    list = list.filter(p => {
      const uname = (p.username || p.display_name || "").toLowerCase();
      return !HIDDEN_USERNAMES.has(uname);
    });

    return list;
  }, [
    rows,
    formats,
    debounced,
    excludeSelf,
    meUid,
    lgsFilter,
    radiusMi,
    myLoc,
    selectedProfileUid
  ]);

  // Derive unique owner list for owner chips (exclude duplicates)
  const cardOwnerChips = useMemo(() => {
    const set = new Set<string>();
    for (const c of cardsListings) {
      if (HIDDEN_OWNER_UIDS.has(c.ownerUid)) continue;
      if (excludeSelf && meUid && c.ownerUid === meUid) continue;
      set.add(c.ownerUid);
      if (set.size >= 200) break; // cap
    }
    return Array.from(set);
  }, [cardsListings, excludeSelf, meUid]);

  // Derive profiles to show in the top profile selector, scoped to current view
  const profilesForSelector = useMemo(() => {
    // Helper to resolve uid -> Profile (skip hidden / missing)
    const resolve = (uid: string | null) => (uid ? rows.find(r => r.uid === uid) || null : null);

    if (view === "cards") {
      // show only owners who currently have visible card listings
      const uids = cardOwnerChips; // already excludes self/bots and caps at 200
      const out: Profile[] = [];
      const seen = new Set<string>();
      for (const uid of uids) {
        if (seen.has(uid)) continue;
        const p = resolve(uid);
        if (p) {
          out.push(p);
          seen.add(uid);
        }
      }
      // Ensure selected profile (if any) is included
      if (selectedProfileUid && !seen.has(selectedProfileUid)) {
        const p = resolve(selectedProfileUid);
        if (p) out.unshift(p);
      }
      return out;
    }

    // For feed / grid / events use the filtered profile list (already applies formats, radius, search, LGS, etc.)
    const base = filteredProfiles || [];
    const map = new Map<string, Profile>();
    for (const p of base) {
      if (!map.has(p.uid)) map.set(p.uid, p);
      if (map.size >= 200) break;
    }
    // Ensure selected profile is present
    if (selectedProfileUid && !map.has(selectedProfileUid)) {
      const sel = resolve(selectedProfileUid);
      if (sel) map.set(sel.uid, sel);
    }
    return Array.from(map.values()).slice(0, 200);
  }, [view, rows, filteredProfiles, cardOwnerChips, selectedProfileUid]);

  // Base filtered cards (search + radius + selectedProfileUid)
  const cardsFilteredBase = useMemo(() => {
    let list = cardsListings.filter(c => !HIDDEN_OWNER_UIDS.has(c.ownerUid));

    // Global profile filter
    if (selectedProfileUid) {
      list = list.filter(c => c.ownerUid === selectedProfileUid);
    }

    // Radius filter
    if (radiusMi != null && myLoc) {
      list = list.filter(c => {
        if (typeof c.distance === "number") return c.distance <= radiusMi;
        const loc = ownerLocs[c.ownerUid];
        if (!loc) return true; // keep until distance known
        const dmi = distMiles(myLoc.lat, myLoc.lng, loc.lat, loc.lng);
        return dmi <= radiusMi;
      });
    }

    // Text search (debounced)
    if (debounced.trim()) {
      const q = debounced.toLowerCase();
      list = list.filter(c =>
        c.name_lower.includes(q) ||
        (c.set || "").toLowerCase().includes(q) ||
        (c.status || "").toLowerCase().includes(q)
      );
    }

    // Format legality filter (use formats selector to filter cards by scryfall legalities)
    if (formats.length) {
      list = list.filter(c => {
        if (!c.legalities) return false;
        for (const f of formats) {
          const key = FORMAT_KEY_MAP[f] || f.toLowerCase().replace(/[^a-z]/g, "");
          if (c.legalities[key] === "legal") return true;
        }
        return false;
      });
    }

    // Card type filter (only show when specific types selected)
    if (cardTypes.length) {
      list = list.filter(c => {
        if (!c.types || !c.types.length) return false;
        for (const t of cardTypes) {
          if (c.types.includes(t)) return true;
        }
        return false;
      });
    }

    return list;
  }, [cardsListings, selectedProfileUid, radiusMi, myLoc, ownerLocs, debounced, formats, cardTypes]);

  // Owner filter layer
  const cardsAfterOwner = useMemo(() => {
    if (showAllOwners || !cardOwnerFilter) return cardsFilteredBase;
    return cardsFilteredBase.filter(c => c.ownerUid === cardOwnerFilter);
  }, [cardsFilteredBase, showAllOwners, cardOwnerFilter]);

  // Page slice
  const pagedCards = useMemo(
    () => cardsAfterOwner.slice(0, cardsVisibleCount),
    [cardsAfterOwner, cardsVisibleCount]
  );

  // Simple preview image map for grid cards (fallback first card image per owner)
  const cardPreviewImages = useMemo(() => {
    const map: Record<string, string | null> = {};
    for (const c of cardsListings) {
      if (!(c.ownerUid in map)) {
        map[c.ownerUid] = c.image || null;
      }
    }
    return map;
  }, [cardsListings]);

  /* -------------------------------- Render -------------------------------- */
  return (
    <div className="flex h-full flex-col">
      <div className="sticky top-0 z-30 border-b border-white/10 bg-black/80 backdrop-blur-md">
       <div className="w-full bg-indigo-900/40 border-b border-indigo-500/30 text-[11px] text-indigo-100 px-4 py-2 text-center">
         Public read‑only view. Sign in to personalize, filter, follow, and make offers.
       </div>
        <div className="mx-auto w-full max-w-5xl px-3 py-3 relative">
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2 flex-shrink-0">
                {(["feed","cards","grid","events"] as ViewMode[]).map(m => {
                  const active = view === m;
                  return (
                    <button
                      key={m}
                      onClick={() => setView(m)} // allow switching views publicly
                      className={
                        "relative group select-none font-semibold tracking-wide transition-all duration-300 " +
                        "focus:outline-none rounded-xl border text-sm px-5 py-1 flex items-center justify-center shadow-md " +
                        getViewButtonClasses(m, active)
                      }
                      aria-pressed={active}
                    >
                      <span className="drop-shadow-sm">
                        {m === "feed"  && "Feed"}
                        {m === "cards" && "Cards"}
                        {m === "grid"  && "Grid"}
                        {m === "events"&& "Events"}
                      </span>
                    </button>
                  );
                })}
                <button
                  type="button"
                  onClick={() => canInteract && setToolbarCollapsed(!toolbarCollapsed)}
                  aria-expanded={!toolbarCollapsed}
                  aria-controls="community-toolbar"
                  title="Filters disabled in public mode"
                  className={
                    "absolute -left-6 sm:-left-9 top-1/2 -translate-y-1/2 z-30 px-2 py-1 text-white text-2xl font-bold focus:outline-none " +
                    (!canInteract ? "opacity-30 cursor-not-allowed" : "")
                  }
                  disabled
                >
                  {toolbarCollapsed ? "+" : "-"}
                </button>
              </div>

              {/* Titles */}
              {view === "feed" && <><div className="text-lg font-extrabold text-white">Feed</div><div className="text-sm text-white/60">Nearby Users</div></>}
              {view === "cards" && <><div className="text-lg font-extrabold text-white">Cards</div><div className="text-sm text-white/60">Latest listings</div></>}
              {view === "grid" && <><div className="text-lg font-extrabold text-white">Grid</div><div className="text-sm text-white/60">All Users</div></>}
              {view === "events" && <><div className="text-lg font-extrabold text-white">Events</div><div className="text-sm text-white/60">Upcoming Events</div></>}
            </div>

            {/* Toolbar completely disabled (hidden) */}
            <div
              id="community-toolbar"
              className="hidden"
              aria-hidden="true"
            />

            {/* Profile selector chips (visible but non-interactive) */}
            <div className="no-scrollbar flex w-full gap-2 overflow-x-auto pb-1 items-center">
              <button
                type="button"
                disabled
                className="flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium transition border-indigo-400/30 bg-indigo-500/10 text-indigo-200 cursor-not-allowed"
                title="Any (public)"
              >
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/5 border border-white/10 text-xs text-white/60">
                  Any
                </span>
                <span className="truncate">Any</span>
              </button>
              {profilesForSelector.map(p => {
                const name = p.display_name || p.username || p.uid.slice(0,6);
                const avatar = p.avatar_url || null;
                return (
                  <div
                    key={p.uid}
                    className="group flex items-center gap-2 rounded-full border pr-3 pl-1 py-1 text-xs max-w-[200px] border-white/10 bg-black/40 text-white/50 cursor-default"
                    title="Sign in to filter by profile"
                  >
                    <span className="relative inline-flex h-7 w-7 overflow-hidden rounded-full border border-white/10 bg-white/5 shrink-0">
                      {avatar ? (
                        <img src={avatar} alt="" className="h-full w-full object-cover" loading="lazy" draggable={false} />
                      ) : (
                        <span className="flex h-full w-full items-center justify-center text-[10px] text-white/40 font-semibold">
                          {name.slice(0,2).toUpperCase()}
                        </span>
                      )}
                    </span>
                    <span className="truncate">{name}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="mx-auto w-full max-w-5xl flex-1 overflow-y-auto px-2 py-3">
        {view === "feed" && (
          <div className="flex flex-col gap-4">
            {filteredProfiles.map(p => (
              <NewMiniProfileCard
                key={p.uid}
                profile={p}
                readOnly // (If component supports; otherwise harmless prop)
              />
            ))}
            {hasMore && (
              <div className="flex items-center justify-center py-6 text-xs text-white/40">
                Scroll to load more…
              </div>
            )}
          </div>
        )}

        {view === "grid" && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {filteredProfiles.map(p => (
              <div
                key={p.uid}
                className="col-span-1 transition-transform"
              >
                <NewMiniProfileCard
                  profile={p}
                  gridMode
                  compactGrid
                  cardPreviewUrl={cardPreviewImages[p.uid]}
                  readOnly
                />
              </div>
            ))}
          </div>
        )}

        {view === "cards" && (
          <div className="flex flex-col gap-4">
            <div className="text-xs text-white/50">
              Cards: {cardsAfterOwner.length}{cardsLoading && " (loading…)"}
            </div>
            {cardsError && (
              <div className="text-red-400 text-sm border border-red-500/40 rounded p-2">
                {cardsError}
              </div>
            )}
            {cardsAfterOwner.length === 0 && !cardsLoading && !cardsError && (
              <div className="text-center text-sm text-white/60 py-6">
                No card listings to display.
              </div>
            )}
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {pagedCards.map(card => {
                const listing = {
                  id: card.id,
                  name: card.name,
                  image: card.image,
                  status: card.status,
                  price: card.price,
                  foil: card.foil,
                  set: card.set,
                  number: card.number,
                  scryfall_id: card.scryfall_id || undefined,
                  oracle_id: card.oracle_id || undefined,
                };
                const owner = rows.find(r => r.uid === card.ownerUid);
                const profileStub = {
                  uid: card.ownerUid,
                  username: owner?.username ?? undefined,
                  display_name: owner?.display_name ?? undefined,
                  avatar_url: owner?.avatar_url ?? undefined,
                } as any;
                return (
                  <ListingProfileCard
                    key={card.ownerUid + ":" + card.id}
                    profile={profileStub}
                    listing={listing as any}
                    distanceMiles={card.distance ?? null}
                    readOnly
                  />
                );
              })}
            </div>
            {cardsVisibleCount < cardsAfterOwner.length && (
              <div className="flex justify-center pt-2">
                <button
                  className="text-xs rounded px-3 py-1 border border-white/15 bg-white/5 text-white/60 cursor-not-allowed"
                  disabled
                  title="Sign in to load more"
                >
                  More
                </button>
              </div>
            )}
          </div>
        )}

        {view === "events" && (
          <div className="w-full relative">
            <div className="flex justify-end mb-2">
              <button
                ref={calendarToggleRef}
                type="button"
                disabled
                className="rounded px-3 py-1.5 text-xs font-medium border border-white/10 bg-black/30 text-white/40 cursor-not-allowed"
                title="Sign in to filter by date"
              >
                Calendar
              </button>
            </div>
            <EventsPage
              selectedDate={null}
              searchTerm={search}
              radiusMi={null}
              selectedFormats={[]}      // force no format filter
              visibilityFilter="public" // public only
              lgsFilter="all"
              selectedProfileUid={null}
            />
          </div>
        )}
      </div>

      {expandedProfileUid && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-3 sm:p-6">
          <div className="text-white/60 text-xs">
            Profile detail disabled in public mode.
          </div>
        </div>
      )}

      <style>{`
        button[disabled]{opacity:.45 !important;cursor:not-allowed !important;}
      `}</style>
    </div>
  );
}

/* ------------------------------ Helpers ---------------------------------- */
function isLgsProfile(p: Profile) {
  return (p as any).is_lgs ||
    (p as any).account_type === "lgs" ||
    (p.rank_title || "").toLowerCase().includes("lgs");
}

function sameDay(a: Date, b: Date) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

type InlineMonthCalendarProps = {
  selected: Date | null;
  onSelect: (d: Date) => void;
};

function InlineMonthCalendar({ selected, onSelect }: InlineMonthCalendarProps) {
  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth(); // current month only (per spec)
  const first = new Date(year, month, 1);
  const startWeekday = first.getDay(); // 0 Sun
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells: Array<Date | null> = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));

  return (
    <div className="m-3 rounded-lg border border-white/10 bg-black/70 backdrop-blur-md p-3 shadow-lg w-[238px]">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-medium text-white/80">
          {today.toLocaleString(undefined, {
            month: "long",
            year: "numeric",
          })}
        </div>
        {selected && (
          <button
            onClick={() => onSelect(selected)}
            className="text-[10px] text-amber-300 hover:text-amber-200"
            title="Click selected date in grid to clear"
          >
            Selected
          </button>
        )}
      </div>
      <div className="grid grid-cols-7 gap-[2px] text-[10px] text-white/40 mb-1">
        {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
          <div key={`wd-${i}`} className="text-center">
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-[4px] text-xs">
        {cells.map((d, idx) => {
          if (!d)
            return <div key={idx} className="h-7 text-center text-white/15" />;
          const isToday = sameDay(d, today);
            const isSel = selected && sameDay(d, selected);
          return (
            <button
              key={idx}
              onClick={() => onSelect(d)}
              className={
                "h-7 rounded-md flex items-center justify-center transition border text-[11px] " +
                (isSel
                  ? "border-amber-400/60 bg-amber-500/30 text-amber-100"
                  : isToday
                  ? "border-white/30 bg-white/10 text-white"
                  : "border-white/10 bg-white/5 text-white/70 hover:bg-white/10")
              }
              title={
                isSel
                  ? "Click to clear date filter"
                  : "Filter events by this date"
              }
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>
      <div className="mt-2 flex justify-between">
        <div className="flex items-center gap-2">
          <span className="h-3 w-3 rounded-sm bg-white/30 border border-white/30" />
          <span className="text-[10px] text-white/50">Today</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="h-3 w-3 rounded-sm bg-amber-500/40 border border-amber-400/60" />
          <span className="text-[10px] text-white/50">Selected</span>
        </div>
      </div>
    </div>
  );
}

// Add this helper inside the file (outside component or above return)
function getViewButtonClasses(m: ViewMode, active: boolean) {
  const base = "view-btn-base ";
  switch (m) {
    case "feed":
      return base + (active ? "view-feed" : "view-feed inactive");
    case "cards":
      return base + (active ? "view-cards" : "view-cards inactive");
    case "grid":
      return base + (active ? "view-grid" : "view-grid inactive");
    case "events":
      return base + (active ? "view-events" : "view-events inactive");
    default:
      return base;
  }
}


